import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { SectionLabel, DeltaText, Sparkles, PlayCircle } from "@/components/ui-bits";
import { RefreshCw, History, Search } from "lucide-react";
import "@/components/meos-extra.css";

export default function BriefPanel() {
  const [brief, setBrief] = useState(null);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/briefings", { params: q ? { q } : {} });
    setHistory(data.items);
  }, [q]);

  useEffect(() => {
    api.get("/briefings/today").then(({ data }) => setBrief(data));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const regenerate = async () => {
    setBusy(true);
    const { data } = await api.post("/briefings/generate");
    setBrief(data);
    await loadHistory();
    setBusy(false);
  };

  const openHistory = async (id) => {
    const { data } = await api.get(`/briefings/${id}`);
    setBrief(data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!brief) return <div className="empty-state">Preparing today's executive brief…</div>;
  const b = brief.brief;

  return (
    <>
      <div className="toolbar">
        <div className="section-label" style={{ margin: 0 }}>Executive morning brief · {brief.date}</div>
        <div className="spacer" />
        <span className="source-badge src-live">EII {brief.eii?.score} · {brief.eii?.status}</span>
        <button className="btn-ghost" onClick={regenerate} disabled={busy} data-testid="regenerate-brief-button">
          <RefreshCw size={13} /> {busy ? "Regenerating…" : "Regenerate"}
        </button>
      </div>

      <div className="advisor-brief" data-testid="brief-today">
        <div className="brief-greeting"><Sparkles size={18} color="#1F75A8" /> {b.greeting}</div>
        <div className="brief-text">{b.happened}</div>
        {b.sections.map((s, i) => (
          <div className="brief-section" key={i}>
            <div className="brief-section-label">{s.label}</div>
            <div className="brief-text">{s.text}</div>
          </div>
        ))}
      </div>

      {brief.references?.length > 0 && (
        <>
          <SectionLabel>Institutional memory — decisions referenced</SectionLabel>
          <div className="card">
            {brief.references.map((r, i) => (
              <div className="ref-item" key={i}><History size={14} color="#1F75A8" style={{ flexShrink: 0, marginTop: 2 }} /> {r}</div>
            ))}
          </div>
        </>
      )}

      {brief.key_metrics && (
        <>
          <SectionLabel>Snapshot — today's KPIs</SectionLabel>
          <div className="metric-grid">
            {Object.entries(brief.key_metrics).map(([k, v]) => (
              <div className="metric-card" key={k}><div className="mc-label">{k}</div><div className="mc-value" style={{ fontSize: 18 }}>{v}</div></div>
            ))}
          </div>
        </>
      )}

      <SectionLabel>Recommended actions</SectionLabel>
      <div className="card">
        {(brief.actions || []).map((a, i) => (
          <div className="action-row" key={i}>
            <PlayCircle size={16} className="action-icon" />
            <div><div className="action-title">{a.title}</div><div className="action-desc">{a.desc}</div></div>
          </div>
        ))}
      </div>

      <SectionLabel>Briefing history</SectionLabel>
      <div className="toolbar">
        <div style={{ position: "relative", flex: 1 }}>
          <Search size={14} style={{ position: "absolute", left: 11, top: 10, color: "#9C978D" }} />
          <input className="filter-input" style={{ width: "100%", paddingLeft: 32 }} placeholder="Search past briefings…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="brief-search" />
        </div>
      </div>
      <div className="card" data-testid="brief-history">
        {history.length === 0 && <div className="empty-state">No briefings match.</div>}
        {history.map((h) => (
          <div className="brief-history-item" key={h.id} onClick={() => openHistory(h.id)}>
            <History size={15} color="#1F75A8" />
            <div style={{ flex: 1 }}>
              <div className="decision-title" style={{ fontWeight: 500 }}>{h.date}</div>
              <div className="decision-meta" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 640 }}>{h.brief?.happened}</div>
            </div>
            <span className="source-badge src-seed">EII {h.eii?.score}</span>
          </div>
        ))}
      </div>
    </>
  );
}
